/**
 * Account form value formatters — ported 1:1 from
 * `_legacy/components/Accounts/helpers.js`. Do NOT refactor the
 * internals without an explicit checklist of invariants — the
 * legacy AccountEdit / AccountCreate submit flows rely on:
 *
 *   - `phone`/`fax` digit-only normalisation
 *   - `intellyChartAllowedSurveys` is pruned to keys still in
 *     `allowedSurveys` (the server refuses a wider list) and emptied
 *     when claims management is off; `accountPermissions.
 *     enableIntellyChart` is then DERIVED from it, never taken from
 *     the form: `true` when at least one assessment is left, `false`
 *     otherwise (the per-assessment ticks replaced the module toggle)
 *   - `allowedResources` entries mapped from `{ key, … }` objects to
 *     plain `key` strings
 *   - `emailConfiguration` / `smsConfiguration` re-expanded from the
 *     flat Formik fields into `[{ local, text, isDefault }]` pairs
 *     per template slot
 *   - When `isDefault=true` is chosen for a pair the `text` key is
 *     stripped so the server uses its own default template
 *     (legacy behaviour reproduced by `formatSmsOrEmailValues`)
 *   - `isShippingTheSame` mirrors `organizationAddress` into
 *     `shippingAddress` before strip
 *   - `checkAccountType` optionally derives `accountType` from the
 *     `isDistributionSponsor` boolean (create flow only; edit flow
 *     leaves the server-supplied value alone)
 *   - `removeUnnecessaryProps` drops helper / flat form keys before
 *     the payload leaves the client
 */
import getDigitPhone from '@shared/lib/phone';
import { ACCOUNT_TYPES } from '../model/constants';
import { pruneClaimAllowList } from './claimAllowLists';

export const removeUnnecessaryProps = values => {
  delete values.isShippingTheSame;
  delete values.isDistributionSponsor;

  delete values.emailSurveyInvitationEnglish;
  delete values.emailSurveyInvitationSpanish;
  delete values.emailResourceInvitationEnglish;
  delete values.emailResourceInvitationSpanish;
  delete values.emailSurveyReminderEnglish;
  delete values.emailSurveyReminderSpanish;
  delete values.emailResourceReminderEnglish;
  delete values.emailResourceReminderSpanish;
  delete values.emailSurveyCompletionEnglish;
  delete values.emailSurveyCompletionSpanish;

  delete values.smsSurveyInvitationEnglish;
  delete values.smsSurveyInvitationSpanish;
  delete values.smsResourceInvitationEnglish;
  delete values.smsResourceInvitationSpanish;
  delete values.smsSurveyReminderEnglish;
  delete values.smsSurveyReminderSpanish;
  delete values.smsResourceReminderEnglish;
  delete values.smsResourceReminderSpanish;
  delete values.smsSurveyCompletionEnglish;
  delete values.smsSurveyCompletionSpanish;

  delete values.emailSurveyInvitationIsDefault;
  delete values.emailResourceInvitationIsDefault;
  delete values.emailSurveyReminderIsDefault;
  delete values.emailResourceReminderIsDefault;
  delete values.emailSurveyCompletionIsDefault;

  delete values.smsSurveyInvitationIsDefault;
  delete values.smsResourceInvitationIsDefault;
  delete values.smsSurveyReminderIsDefault;
  delete values.smsResourceReminderIsDefault;
  delete values.smsSurveyCompletionIsDefault;
  delete values.loading;

  return values;
};

export const formatSmsOrEmailValues = (typeKey, initValues, values) => {
  const formattedValues = JSON.parse(JSON.stringify(values));

  Object.keys(formattedValues[typeKey]).forEach(key => {
    const value = initValues && initValues[typeKey] && initValues[typeKey][key];
    const newValue = formattedValues[typeKey][key];

    const isNewDefault = newValue && newValue[0]?.isDefault;

    if (value && value.length > 0) {
      if (isNewDefault) {
        formattedValues[typeKey][key][0].text = value[0].text;
        formattedValues[typeKey][key][1].text = value[1].text;
      }
    } else if ((value && value.length === 0) || !value) {
      if (isNewDefault) {
        delete formattedValues[typeKey][key][0].text;
        delete formattedValues[typeKey][key][1].text;
      }
    }
  });
  return formattedValues;
};

export const formattedAccountsValues = (values, checkAccountType, initValues) => {
  const claimsOn = values.accountPermissions?.enableClaimsManagement === true;
  const intellyChartAllowedSurveys = claimsOn
    ? pruneClaimAllowList(values.intellyChartAllowedSurveys, values.allowedSurveys)
    : [];
  // The E-Visit list has no module flag derived from it, so it is kept
  // as stored while Claims management is off — only pruned to the
  // allowed list, which the server requires either way.
  const programClaimAllowedSurveys = pruneClaimAllowList(
    values.programClaimAllowedSurveys,
    values.allowedSurveys
  );
  const newAccountPermissions = {
    ...values.accountPermissions,
    enableIntellyChart: intellyChartAllowedSurveys.length > 0,
  };

  let formattedValues = {
    ...values,
    phone: getDigitPhone(values.phone),
    fax: getDigitPhone(values.fax),
    accountPermissions: newAccountPermissions,
    // Only the edit form carries the list; the create payload never
    // had the key and keeps its shape.
    ...(values.intellyChartAllowedSurveys !== undefined && { intellyChartAllowedSurveys }),
    ...(values.programClaimAllowedSurveys !== undefined && { programClaimAllowedSurveys }),
    allowedResources: values.allowedResources.map(item =>
      typeof item === 'object' && item.hasOwnProperty('key') ? item.key : item
    ),
    emailConfiguration: {
      ...initValues?.emailConfiguration,
      surveyInvitation: [
        {
          local: 'english',
          text: values.emailSurveyInvitationEnglish,
          isDefault: values.emailSurveyInvitationIsDefault,
        },
        {
          local: 'spanish',
          text: values.emailSurveyInvitationSpanish,
          isDefault: values.emailSurveyInvitationIsDefault,
        },
      ],
      surveyReminder: [
        {
          local: 'english',
          text: values.emailSurveyReminderEnglish,
          isDefault: values.emailSurveyReminderIsDefault,
        },
        {
          local: 'spanish',
          text: values.emailSurveyReminderSpanish,
          isDefault: values.emailSurveyReminderIsDefault,
        },
      ],
      surveyCompletion: [
        {
          local: 'english',
          text: values.emailSurveyCompletionEnglish,
          isDefault: values.emailSurveyCompletionIsDefault,
        },
        {
          local: 'spanish',
          text: values.emailSurveyCompletionSpanish,
          isDefault: values.emailSurveyCompletionIsDefault,
        },
      ],
      resourceInvitation: [
        {
          local: 'english',
          text: values.emailResourceInvitationEnglish,
          isDefault: values.emailResourceInvitationIsDefault,
        },
        {
          local: 'spanish',
          text: values.emailResourceInvitationSpanish,
          isDefault: values.emailResourceInvitationIsDefault,
        },
      ],
      resourceReminder: [
        {
          local: 'english',
          text: values.emailResourceReminderEnglish,
          isDefault: values.emailResourceReminderIsDefault,
        },
        {
          local: 'spanish',
          text: values.emailResourceReminderSpanish,
          isDefault: values.emailResourceReminderIsDefault,
        },
      ],
    },
    smsConfiguration: {
      ...initValues?.smsConfiguration,
      surveyInvitation: [
        {
          local: 'english',
          text: values.smsSurveyInvitationEnglish,
          isDefault: values.smsSurveyInvitationIsDefault,
        },
        {
          local: 'spanish',
          text: values.smsSurveyInvitationSpanish,
          isDefault: values.smsSurveyInvitationIsDefault,
        },
      ],
      surveyReminder: [
        {
          local: 'english',
          text: values.smsSurveyReminderEnglish,
          isDefault: values.smsSurveyReminderIsDefault,
        },
        {
          local: 'spanish',
          text: values.smsSurveyReminderSpanish,
          isDefault: values.smsSurveyReminderIsDefault,
        },
      ],
      surveyCompletion: [
        {
          local: 'english',
          text: values.smsSurveyCompletionEnglish,
          isDefault: values.smsSurveyCompletionIsDefault,
        },
        {
          local: 'spanish',
          text: values.smsSurveyCompletionSpanish,
          isDefault: values.smsSurveyCompletionIsDefault,
        },
      ],
      resourceInvitation: [
        {
          local: 'english',
          text: values.smsResourceInvitationEnglish,
          isDefault: values.smsResourceInvitationIsDefault,
        },
        {
          local: 'spanish',
          text: values.smsResourceInvitationSpanish,
          isDefault: values.smsResourceInvitationIsDefault,
        },
      ],
      resourceReminder: [
        {
          local: 'english',
          text: values.smsResourceReminderEnglish,
          isDefault: values.smsResourceReminderIsDefault,
        },
        {
          local: 'spanish',
          text: values.smsResourceReminderSpanish,
          isDefault: values.smsResourceReminderIsDefault,
        },
      ],
    },
  };

  if (checkAccountType) {
    formattedValues.accountType = values.isDistributionSponsor
      ? ACCOUNT_TYPES.DISTRIBUTION_SPONSOR
      : ACCOUNT_TYPES.STANDARD;
  }

  if (values.isShippingTheSame) {
    formattedValues.shippingAddress = {
      ...formattedValues.organizationAddress,
    };
  }

  formattedValues = removeUnnecessaryProps(formattedValues);
  formattedValues = formatSmsOrEmailValues('emailConfiguration', initValues, formattedValues);
  formattedValues = formatSmsOrEmailValues('smsConfiguration', initValues, formattedValues);

  return formattedValues;
};

export const getAllIndexes = (arr, val) => {
  const indexes = [];
  let i = -1;
  // eslint-disable-next-line no-cond-assign
  while ((i = arr.indexOf(val, i + 1)) !== -1) {
    indexes.push(i);
  }
  return indexes;
};
